import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  lifecycleResponseSchema,
  type LifecycleResponse,
} from "@community-analytics/shared";

import { apiFetch } from "@shared/api";

import { memberQueryKeys } from "./keys";

/**
 * GET /api/communities/:id/lifecycle — TASK-033.
 *
 * Drives the Mini App "Lifecycle" page's stacked-area chart. Returns the
 * trailing 30-day stage-distribution trend for the active community.
 *
 * NOT plan-gated — even FREE owners get the full trend (per the
 * backend's services/community-lifecycle.ts computation policy). The
 * lifecycle distribution is the §6.3.2 onboarding insight; gating it
 * would defeat the primary "is my community drifting" signal.
 *
 * The hook is a no-op when `communityId` is null (e.g. before the
 * communities list resolves and the active-community store picks one) —
 * `enabled: false` keeps TanStack Query from firing a request with a
 * malformed URL. Same shape as `useHealthQuery` / `useOverviewQuery`.
 */
export function useLifecycleQuery(
  communityId: string | null,
): UseQueryResult<LifecycleResponse, Error> {
  return useQuery<LifecycleResponse, Error>({
    queryKey: memberQueryKeys.lifecycle(communityId ?? "__noop__"),
    enabled: communityId !== null,
    queryFn: async () => {
      if (communityId === null) {
        // Unreachable in production — `enabled` gates the call. Re-asserted
        // for the type narrower so we don't have to non-null-assert below.
        throw new Error("communityId is required");
      }
      const raw = await apiFetch<unknown>(
        `/api/communities/${encodeURIComponent(communityId)}/lifecycle`,
      );
      return lifecycleResponseSchema.parse(raw);
    },
  });
}
